import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Chip, Typography } from '@mui/material'
import PlanService from '../../services/PlanService'
import TipContext from '../../providers/GlobalTip'
import MonthlyPlanningGrid from './MonthlyPlanningGrid'
import PlanEditMonthlyHeader from './PlanEditMonthlyHeader'
import { deriveMonthlyInflows, rollupWeeklyToMonthly } from '../../lib/PlannerRollup'
import { rollupDailyToMonths, applyEditAggregate, applyEditAllocateScopeLocked } from '../../lib/PlannerDailyModel'
import { buildPlannerAnnualValues } from '../../lib/PlannerAnnualValues'
import { deriveMonthlyMarkdownPercent, deriveMonthlyMarkdownValue, sumMonthlyMarkdownPlan } from '../../lib/PlannerMonthlyMarkdown'
import { deriveTrendedTurnFromPeriodData, formatMonthLabelWithSaturdayComparison, formatRepresentativeMonthLabel } from '../../lib/PlannerWeeklyView'

export const resolveMonthlyHeaderTrendedTurn = ({ weeklyPeriodData = [], fallbackAnnualValues = null }) => {
  // Header metric should reflect the full visible weekly period, including rear historical rows.
  const weeklyDrivenValue = deriveTrendedTurnFromPeriodData(weeklyPeriodData, { includeRearHistorical: true })
  if (weeklyDrivenValue !== null) {
    return {
      value: weeklyDrivenValue,
      source: 'weekly',
    }
  }

  const fallbackValue = Number(fallbackAnnualValues?.trendedTurn)
  if (Number.isFinite(fallbackValue)) {
    return {
      value: fallbackValue,
      source: 'monthly',
    }
  }

  return {
    value: null,
    source: null,
  }
}

export const hasMeaningfulMonthlyPlanValues = (row) => (
  Number(row?.salesPlan ?? row?.sales_monthly_plan ?? row?.salesMonthlyPlan ?? 0) !== 0 ||
  Number(row?.markdownPlan ?? row?.markdown_plan ?? row?.markdownPlan ?? 0) !== 0 ||
  Number(row?.planInvBOM ?? row?.inventory_bom_plan ?? row?.inventoryBomPlan ?? 0) !== 0 ||
  Number(row?.planInflowRetail ?? row?.inflow_retail_plan ?? row?.inflowRetailPlan ?? 0) !== 0 ||
  Number(row?.planInflowCost ?? row?.inflow_cost_plan ?? row?.inflowCostPlan ?? 0) !== 0
)

export const shouldUseDailyMonthValues = ({ monthHasDaily, dailySales, dailyMarkdown, dailyBom, monthlyRow }) => {
  if (!monthHasDaily) return false
  const dailyHasMeaningfulPlanValues = Number(dailySales || 0) !== 0 || Number(dailyMarkdown || 0) !== 0 || Number(dailyBom || 0) !== 0
  if (dailyHasMeaningfulPlanValues) return true
  return !hasMeaningfulMonthlyPlanValues(monthlyRow)
}

export const buildMonthlySummaryValues = (mergedData) => {
  const safeRows = Array.isArray(mergedData) ? mergedData : []
  const sum = (k) => safeRows.reduce((acc, x) => acc + (Number(x?.[k]) || 0), 0)
  const avg = (k) => safeRows.length ? (sum(k) / safeRows.length) : 0
  const sumPlanPct = safeRows.reduce((acc, x) => acc + (Number(x?.planPercent) || 0), 0)
  const sumActualPct = safeRows.reduce((acc, x) => acc + (Number(x?.actualPercent) || 0), 0)
  const sumPlanMdPct = safeRows.reduce((acc, x) => acc + (Number(x?.markdownPlanPercent) || 0), 0)
  const sumActualMdPct = safeRows.reduce((acc, x) => acc + (Number(x?.markdownActualPercent) || 0), 0)
  const totalSalesPlan = sum('salesPlan')
  const totalSalesActual = sum('salesActual')
  const avgActualInvBOM = avg('actualInvBOM')
  const avgPlanInvBOM = avg('planInvBOM')

  return {
    salesActual: totalSalesActual,
    salesPlan: totalSalesPlan,
    planPercent: sumPlanPct,
    actualPercent: sumActualPct,
    actualInvBOM: avgActualInvBOM,
    actualSS: totalSalesActual ? (avgActualInvBOM / totalSalesActual) : 0,
    planSS: totalSalesPlan ? (avgPlanInvBOM / totalSalesPlan) : 0,
    planInvBOM: avgPlanInvBOM,
    planInflowRetail: sum('planInflowRetail'),
    planInflowCost: sum('planInflowCost'),
    inflowRetailActual: sum('inflowRetailActual'),
    inflowCostActual: sum('inflowCostActual'),
    onOrderRetailMtd: sum('onOrderRetailMtd'),
    onOrderCostMtd: sum('onOrderCostMtd'),
    markdownActual: sum('markdownActual'),
    markdownPlan: sum('markdownPlan'),
    markdownPlanPercent: sumPlanMdPct,
    markdownActualPercent: sumActualMdPct,
  }
}

const hasMeaningfulActualValue = (value) => {
  const n = Number(value)
  return Number.isFinite(n) && Math.abs(n) > 0.000001
}

export const buildMonthlyActualInflowsFromWeeklyPeriods = ({ weeklyPeriodData = [], weekToMonthDayCounts = new Map() }) => {
  const byMonth = new Map()

  const addToMonth = (monthKey, retailValue, costValue) => {
    const normalizedMonthKey = String(monthKey || '')
    if (!normalizedMonthKey) return
    const current = byMonth.get(normalizedMonthKey) || { inflowRetailActual: 0, inflowCostActual: 0 }
    current.inflowRetailActual += Number(retailValue || 0)
    current.inflowCostActual += Number(costValue || 0)
    byMonth.set(normalizedMonthKey, current)
  }

  ;(Array.isArray(weeklyPeriodData) ? weeklyPeriodData : []).forEach((row) => {
    const retailActual = Number(row?.actualInflowRetail || 0)
    const costActual = Number(row?.actualInflowCost || 0)
    if (!hasMeaningfulActualValue(retailActual) && !hasMeaningfulActualValue(costActual)) return

    const epochWeekKey = String(row?.period || '')
    const monthMap = weekToMonthDayCounts?.get?.(Number(epochWeekKey)) || weekToMonthDayCounts?.get?.(epochWeekKey) || null

    if (monthMap && typeof monthMap.entries === 'function' && monthMap.size > 0) {
      const monthEntries = Array.from(monthMap.entries())
      const totalDays = monthEntries.reduce((acc, [, days]) => acc + Number(days || 0), 0)
      if (totalDays > 0) {
        monthEntries.forEach(([epochMonth, days]) => {
          const share = Number(days || 0) / totalDays
          addToMonth(epochMonth, retailActual * share, costActual * share)
        })
        return
      }
    }

    addToMonth(row?.representativeMonthKey, retailActual, costActual)
  })

  return byMonth
}

const toLegacyDetailRecord = (payload) => {
  if (Array.isArray(payload)) return payload[0] || null
  if (Array.isArray(payload?.data)) return payload.data[0] || null
  if (payload?.data && typeof payload.data === 'object') return payload.data
  return payload && typeof payload === 'object' ? payload : null
}

const parseLegacyActualArray = (value, length = 12) => {
  const parts = value === null || typeof value === 'undefined' || value === ''
    ? []
    : String(value).split(',').map((part) => Number(String(part).trim()) || 0)
  return Array.from({ length }, (_, idx) => Number(parts[idx] || 0))
}

export const buildMonthlyActualRowsFromLegacyDetail = ({ legacyDetail = null, epochMonth }) => {
  const record = toLegacyDetailRecord(legacyDetail)
  if (!record) return []

  const salesActuals = parseLegacyActualArray(record?.SalesL12)
  const inventoryActuals = parseLegacyActualArray(record?.InventoryL12)
  const inflowRetailActuals = parseLegacyActualArray(record?.InflowRetailL12)
  const inflowCostActuals = parseLegacyActualArray(record?.InflowCostL12)
  const markdownActuals = parseLegacyActualArray(record?.MarkdownsL12)

  if (hasMeaningfulActualValue(record?.InventoryActualThisMonth)) {
    inventoryActuals[0] = Number(record?.InventoryActualThisMonth || 0)
  }

  const start = Number(epochMonth || 0)
  return Array.from({ length: 12 }, (_, idx) => {
    const salesActual = Number(salesActuals[idx] || 0)
    const actualInvBOM = Number(inventoryActuals[idx] || 0)
    return {
      epochMonth: start + idx,
      salesActual,
      markdownActual: Number(markdownActuals[idx] || 0),
      actualInvBOM,
      actualSS: salesActual ? (actualInvBOM / salesActual) : 0,
      inflowRetailActual: Number(inflowRetailActuals[idx] || 0),
      inflowCostActual: Number(inflowCostActuals[idx] || 0),
    }
  })
}

const getRowEpochMonthKey = (row) => String(row?.epochMonth ?? row?.plan_epoch_month ?? row?.planEpochMonth ?? '')

const resolveMonthFieldValue = ({ epochMonth, monthRoll = {}, datesByMonth = new Map(), fallbackRows = [], field }) => {
  const monthKey = String(epochMonth ?? '')
  const datesForMonth = datesByMonth?.get?.(monthKey) || []
  if (datesForMonth.length) return Number(monthRoll?.[monthKey]?.[field] || 0)

  const fallbackRow = (Array.isArray(fallbackRows) ? fallbackRows : []).find((row) => getRowEpochMonthKey(row) === monthKey) || null
  return Number(fallbackRow?.[field] || 0)
}

export const resolveAnnualMonthTotal = ({ epochMonths = [], monthRoll = {}, datesByMonth = new Map(), fallbackRows = [], field }) => {
  return (Array.isArray(epochMonths) ? epochMonths : []).reduce((acc, epochMonthKey) => {
    return acc + resolveMonthFieldValue({
      epochMonth: epochMonthKey,
      monthRoll,
      datesByMonth,
      fallbackRows,
      field,
    })
  }, 0)
}

export const derivePlanInventoryPairFromEdit = ({ salesPlan, field, value }) => {
  const safeSalesPlan = Math.max(0, Number(salesPlan || 0))

  if (field === 'planSS') {
    const nextPlanSS = Math.max(0, Number(value || 0))
    return {
      planInvBOM: safeSalesPlan * nextPlanSS,
      planSS: safeSalesPlan > 0 ? nextPlanSS : 0,
    }
  }

  if (field === 'planInvBOM') {
    const nextPlanInvBOM = Math.max(0, Number(value || 0))
    return {
      planInvBOM: nextPlanInvBOM,
      planSS: safeSalesPlan > 0 ? (nextPlanInvBOM / safeSalesPlan) : 0,
    }
  }

  return null
}

export const hasExplicitPlanInventoryOverrideForMonth = ({
  epochMonth,
  monthlyEditsByMonth = {},
  weeklyEditsByWeek = {},
  weekToMonthDayCounts = new Map(),
}) => {
  const monthKey = String(epochMonth ?? '')
  if (!monthKey) return false

  const monthlyEdit = monthlyEditsByMonth?.[monthKey]
  if (typeof monthlyEdit?.planInvBOM !== 'undefined' || typeof monthlyEdit?.planSS !== 'undefined') {
    return true
  }

  return Object.entries(weeklyEditsByWeek || {}).some(([epochWeekKey, edit]) => {
    if (typeof edit?.planInvBOM === 'undefined') return false
    if (Array.isArray(edit?.targetEpochMonths) && edit.targetEpochMonths.length) {
      return edit.targetEpochMonths.map((value) => String(value)).includes(monthKey)
    }
    const monthMap = weekToMonthDayCounts?.get?.(Number(epochWeekKey)) || weekToMonthDayCounts?.get?.(String(epochWeekKey))
    if (!monthMap?.entries) return false
    return Array.from(monthMap.keys()).some((mappedEpochMonth) => String(mappedEpochMonth) === monthKey)
  })
}

export const buildAnnualTurnTargets = (rows, newTurn) => {
  const safeRows = Array.isArray(rows) ? rows : []
  if (!safeRows.length) return []

  const safeTurn = Number(newTurn || 0)
  const totalSales = safeRows.reduce((acc, row) => acc + Number(row?.salesPlan || 0), 0)
  const desiredAvgBom = safeTurn > 0 ? (totalSales / safeTurn) : 0
  const currentAvgBom = safeRows.length
    ? (safeRows.reduce((acc, row) => acc + Number(row?.planInvBOM || 0), 0) / safeRows.length)
    : 0

  let nextBoms = []

  if (currentAvgBom > 0) {
    const scale = desiredAvgBom / currentAvgBom
    nextBoms = safeRows.map((row) => Math.max(0, Number(row?.planInvBOM || 0) * scale))
  } else {
    const shape = safeRows.map((row) => {
      const salesPlan = Math.max(0, Number(row?.salesPlan || 0))
      return salesPlan > 0 ? salesPlan : 1
    })
    const shapeAvg = shape.length ? (shape.reduce((acc, value) => acc + value, 0) / shape.length) : 0
    const scale = shapeAvg > 0 ? (desiredAvgBom / shapeAvg) : 0
    nextBoms = shape.map((value) => Math.max(0, value * scale))
  }

  return safeRows.map((row, idx) => {
    const salesPlan = Number(row?.salesPlan || 0)
    const planInvBOM = Number(nextBoms[idx] || 0)
    return {
      planInvBOM,
      planSS: salesPlan ? (planInvBOM / salesPlan) : 0,
    }
  })
}

const allocateRoundedTotalByWeights = (total, rawWeights) => {
  const safeTotal = Math.max(0, Math.round(Number(total || 0)))
  const weights = Array.isArray(rawWeights) ? rawWeights.map((value) => Math.max(0, Number(value) || 0)) : []
  const weightSum = weights.reduce((acc, value) => acc + value, 0)
  const normalized = weights.length
    ? (weightSum > 0 ? weights.map((value) => value / weightSum) : weights.map(() => 1 / weights.length))
    : []

  const rawAllocations = normalized.map((weight) => safeTotal * weight)
  const floors = rawAllocations.map((value) => Math.floor(value))
  let remainder = safeTotal - floors.reduce((acc, value) => acc + value, 0)

  const rankedFractions = rawAllocations
    .map((value, idx) => ({ idx, fraction: value - floors[idx] }))
    .sort((a, b) => {
      if (b.fraction !== a.fraction) return b.fraction - a.fraction
      return a.idx - b.idx
    })

  const allocations = [...floors]
  let cursor = 0
  while (remainder > 0 && rankedFractions.length) {
    allocations[rankedFractions[cursor % rankedFractions.length].idx] += 1
    remainder -= 1
    cursor += 1
  }

  return allocations
}

export const buildAnnualMarkdownTargets = (rows, newPct) => {
  const safeRows = Array.isArray(rows) ? rows : []
  if (!safeRows.length) return []

  const totalSales = safeRows.reduce((acc, row) => acc + Math.max(0, Number(row?.salesPlan || 0)), 0)
  const desiredTotalMd = Math.max(0, totalSales * (Number(newPct || 0) / 100))
  const salesWeights = safeRows.map((row) => Math.max(0, Number(row?.salesPlan || 0)))
  const markdownPlanValues = allocateRoundedTotalByWeights(desiredTotalMd, salesWeights)
  const annualMarkdownPlan = markdownPlanValues.reduce((acc, value) => acc + Number(value || 0), 0)

  return safeRows.map((row, idx) => ({
    markdownPlan: Number(markdownPlanValues[idx] || 0),
    markdownPlanPercent: deriveMonthlyMarkdownPercent({
      markdownPlan: Number(markdownPlanValues[idx] || 0),
      annualMarkdownPlan,
    }),
  }))
}

export const buildAnnualSalesTargets = (rows, newAnnualSales) => {
  const safeRows = Array.isArray(rows) ? rows : []
  if (!safeRows.length) return []

  const currentAnnualSales = safeRows.reduce((acc, row) => acc + Number(row?.salesPlan || 0), 0)
  const monthWeights = safeRows.map((row) => {
    if (currentAnnualSales > 0) return Number(row?.salesPlan || 0) / currentAnnualSales
    return 1 / Math.max(safeRows.length, 1)
  })
  const safeAnnualSales = Math.max(0, Number(newAnnualSales || 0))

  const desiredMarkdownByMonth = safeRows.map((row, idx) => {
    const desiredSales = safeAnnualSales * monthWeights[idx]
    const mdRate = Number(row?.salesPlan || 0) ? (Number(row?.markdownPlan || 0) / Number(row?.salesPlan || 0)) : 0
    return Math.round(desiredSales * mdRate)
  })
  const desiredTotalMd = desiredMarkdownByMonth.reduce((acc, value) => acc + Number(value || 0), 0)

  return safeRows.map((row, idx) => {
    const desiredSales = safeAnnualSales * monthWeights[idx]
    const desiredMd = Number(desiredMarkdownByMonth[idx] || 0)
    const desiredBom = Math.max(0, desiredSales * Number(row?.planSS || 0))

    return {
      salesPlan: desiredSales,
      markdownPlan: desiredMd,
      markdownPlanPercent: deriveMonthlyMarkdownPercent({ markdownPlan: desiredMd, annualMarkdownPlan: desiredTotalMd }),
      planInvBOM: desiredBom,
      planPercent: safeAnnualSales ? (desiredSales / safeAnnualSales) * 100 : 0,
      planSS: desiredSales ? (desiredBom / desiredSales) : 0,
    }
  })
}

function PlanEditMonthlyGridShell({ clientId, epochMonth, locationClassId, monthlyPlanSettingsId, planVersion, plannerSession, weeklyPeriodData = [], onMonthlyEdit, onMonthlyContextLoaded, onMonthlyBaseUi, annualHeaderCollapsed = false, onAnnualHeaderCollapsedChange }) {
  const messageContext = TipContext.useMessage()

  const toImuRate = (value) => {
    const n = Number(value || 0)
    if (!Number.isFinite(n) || n <= 0) return 0
    return n > 1 ? (n / 100) : n
  }

  const calculateMonthlyFreshnessFactor = (bowInventory, priorThreeMonthInflows) => {
    const inventory = Number(bowInventory)
    if (!Number.isFinite(inventory)) return null
    const denominator = (Array.isArray(priorThreeMonthInflows) ? priorThreeMonthInflows : [])
      .reduce((acc, value) => acc + (Number.isFinite(Number(value)) ? Number(value) : 0), 0)
    if (!(denominator > 0)) return null
    return inventory / denominator
  }

  const applyFirstDayInventory = (byDate, dates, nextValue) => {
    if (!byDate || typeof byDate.get !== 'function' || !Array.isArray(dates) || !dates.length) return byDate
    const nextDaily = new Map(byDate)
    const firstDate = dates[0]
    const record = nextDaily.get(firstDate)
    if (!record) return byDate
    nextDaily.set(firstDate, {
      ...record,
      inventory: Math.max(0, Number(nextValue || 0)),
    })
    return nextDaily
  }

  const canLoad = useMemo(() => {
    return !!(clientId && epochMonth && locationClassId)
  }, [clientId, epochMonth, locationClassId])

  const monthLabels = useMemo(() => {
    if (!epochMonth) return Array.from({ length: 12 }, (_, i) => `M${i + 1}`)
    const start = Number(epochMonth)
    return Array.from({ length: 12 }, (_, i) => {
      const em = start + i
      return formatMonthLabelWithSaturdayComparison(em, null, formatRepresentativeMonthLabel(em, null))
    })
  }, [epochMonth])

  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState([])
  const [legacyActualRows, setLegacyActualRows] = useState([])
  const [dirty, setDirty] = useState(false)
  const [draft, setDraft] = useState(() => ({
    salesPlan: [],
    planPercent: [],
    planSS: [],
    planInvBOM: [],
    markdownPlanPercent: [],
    markdownPlan: [],
  }))

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      if (!canLoad) {
        setRows([])
        setLegacyActualRows([])
        return
      }

      try {
        setLoading(true)
        const resp = await PlanService.getMonthlyPlanContext(clientId, {
          locationClassId,
          epochMonth,
          monthsForward: 11,
          monthlyPlanSettingsId,
          planVersion,
        })
        const data = resp?.data ?? resp ?? []
        const all = Array.isArray(data) ? data : []
        console.info('[planner][diag] getMonthlyPlanContext', {
          clientId,
          locationClassId,
          epochMonth,
          monthlyPlanSettingsId,
          planVersion,
          rowCount: all.length,
          firstRow: all.length ? {
            plan_epoch_month: all[0]?.plan_epoch_month,
            location_class_id: all[0]?.location_class_id,
            plan_settings_id: all[0]?.plan_settings_id,
            plan_version: all[0]?.plan_version,
            sales_monthly_plan: all[0]?.sales_monthly_plan,
          } : null,
        })
        // Response includes skeleton rows with plan_epoch_month even if no plan exists yet.
        // Keep both keys so downstream mapping works regardless of column naming.
        const cleaned = all.map((r) => ({
          ...r,
          plan_epoch_month: r?.plan_epoch_month ?? r?.plan_epoch_month ?? r?.planEpochMonth,
        }))

        const resolvedPlanSettingsId = monthlyPlanSettingsId
          || cleaned.find((row) => row?.plan_settings_id)?.plan_settings_id
          || null

        let nextLegacyActualRows = []
        if (resolvedPlanSettingsId) {
          try {
            const legacyDetailPayload = await PlanService.getLivePlanDetailV2(
              clientId,
              resolvedPlanSettingsId,
              epochMonth,
              locationClassId,
            )
            nextLegacyActualRows = buildMonthlyActualRowsFromLegacyDetail({
              legacyDetail: legacyDetailPayload,
              epochMonth,
            })
          } catch (legacyError) {
            console.error('[planner][diag] getLivePlanDetailV2 failed', legacyError)
          }
        }

        if (cancelled) return
        setRows(cleaned)
        setLegacyActualRows(nextLegacyActualRows)
      } catch (e) {
        if (!cancelled) {
          messageContext.show(e?.response?.data?.message || 'Failed to load monthly plan context')
          setRows([])
          setLegacyActualRows([])
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()

    return () => {
      cancelled = true
    }
  }, [canLoad, clientId, locationClassId, epochMonth, monthlyPlanSettingsId, planVersion])

  const columns = useMemo(() => {
    const cur = Number(epochMonth || 0)
    return monthLabels.map((label, idx) => {
      const em = cur + idx
      return {
        key: String(em),
        label,
        index: idx,
        isCurrent: idx === 0,
        isFuture: idx > 0,
      }
    })
  }, [monthLabels, epochMonth])

  // Build 12-month array from context rows.
  const data = useMemo(() => {
    const start = Number(epochMonth || 0)

    const byMonth = new Map()
    ;(rows || []).forEach((r) => {
      const em = Number(r?.plan_epoch_month ?? r?.planEpochMonth)
      if (!Number.isFinite(em)) return
      byMonth.set(em, r)
    })

    const loadedRows = Array.from({ length: 12 }, (_, i) => {
      const em = start + i
      const r = byMonth.get(em) || {}

      const salesPlan = Number(r?.sales_monthly_plan ?? r?.salesMonthlyPlan ?? r?.salesPlan ?? 0)
      const markdownPlan = Number(r?.markdown_plan ?? r?.markdownPlan ?? 0)
      const inventoryBomPlan = Number(r?.inventory_bom_plan ?? r?.inventoryBomPlan ?? r?.planInvBOM ?? 0)
      const inflowRetailPlan = Number(r?.inflow_retail_plan ?? r?.inflowRetailPlan ?? 0)
      const inflowCostPlan = Number(r?.inflow_cost_plan ?? r?.inflowCostPlan ?? 0)
      const planSS = Number(r?.stock_to_sales_plan ?? r?.stockToSalesPlan ?? r?.planSS ?? 0)
      const planPercent = Number(r?.sales_monthly_percentage_plan ?? 0) * 100
      // Actuals: prefer context fields if present; otherwise 0.
      const salesActual = Number(r?.sales_actual ?? r?.salesActual ?? 0)
      const markdownActual = Number(r?.markdown_actual ?? r?.markdownActual ?? 0)
      const actualInvBOM = Number(r?.inventory_actual_bom ?? r?.inventoryActualBom ?? r?.inventory_bom_actual ?? r?.inventoryBomActual ?? 0)
      const actualSS = Number(
        r?.stock_to_sales_actual ?? r?.stockToSalesActual ?? r?.Actual_StockToSales ?? r?.actual_stock_to_sales ?? 0
      )

      const inflowRetailActual = Number(r?.inflow_retail_actual ?? r?.inflowRetailActual ?? r?.inflowRetail ?? 0)
      const inflowCostActual = Number(r?.inflow_cost_actual ?? r?.inflowCostActual ?? r?.inflowCost ?? 0)
      const onOrderRetailMtd = Number(r?.onorderretailmtd ?? r?.onOrderRetailMTD ?? 0)
      const onOrderCostMtd = Number(r?.onordercostmtd ?? r?.onOrderCostMTD ?? 0)

      const markdownPct = salesPlan ? (markdownPlan / salesPlan) * 100 : 0

      return {
        epochMonth: em,
        salesActual,
        salesPlan,
        planPercent,
        actualPercent: salesPlan ? (salesActual / salesPlan) * 100 : 0,
        markdownActual,
        markdownPlan,
        markdownPlanPercent: 0,
        markdownActualPercent: markdownPlan ? (markdownActual / markdownPlan) * 100 : 0,
        actualInvBOM,
        actualSS,
        planSS,
        planInvBOM: inventoryBomPlan,
        planInflowRetail: inflowRetailPlan,
        planInflowCost: inflowCostPlan,
        inflowRetailActual,
        inflowCostActual,
        onOrderRetailMtd,
        onOrderCostMtd,
        markdownPct,
        salesAnnualPlan: Number(r?.sales_annual_plan ?? r?.salesAnnualPlan ?? 0),
        imuPlan: Number(r?.imu_plan ?? r?.imuPlan ?? 0),
        turnPlan: Number(r?.turn_plan ?? r?.turnPlan ?? 0),
        planSettingsId: r?.plan_settings_id ?? null,
      }
    })

    console.info('[planner][diag] monthly-grid data shape', {
      epochMonth: start,
      inputRows: Array.isArray(rows) ? rows.length : 0,
      renderedRows: loadedRows.length,
      firstRendered: loadedRows.length ? {
        epochMonth: loadedRows[0]?.epochMonth,
        salesPlan: loadedRows[0]?.salesPlan,
        markdownPlan: loadedRows[0]?.markdownPlan,
        planInvBOM: loadedRows[0]?.planInvBOM,
      } : null,
    })

    const annualMarkdownPlan = sumMonthlyMarkdownPlan(loadedRows)
    return loadedRows.map((row) => ({
      ...row,
      markdownPlanPercent: deriveMonthlyMarkdownPercent({
        markdownPlan: Number(row?.markdownPlan || 0),
        annualMarkdownPlan,
      }),
    }))
  }, [rows, epochMonth])

  const lastRowsSigRef = useRef('')
  useEffect(() => {
    // Provide raw context rows to the outer page so it can build day weights from daily_values.
    // Guard against parent re-renders that recreate callback identities.
    const sig = Array.isArray(rows)
      ? rows.map((r) => `${r?.plan_settings_id ?? ''}:${r?.plan_epoch_month ?? r?.planEpochMonth ?? ''}:${r?.created_date?.date ?? r?.created_date ?? ''}:${r?.plan_version ?? ''}`).join('|')
      : ''

    if (sig === lastRowsSigRef.current) return
    lastRowsSigRef.current = sig

    if (typeof onMonthlyContextLoaded === 'function') {
      onMonthlyContextLoaded(rows)
    }
  }, [rows])

  // onMonthlyBaseUi is now wired to mergedData (after that useMemo is defined below)
  // so that live cell edits immediately drive the left-panel chart.

  const monthlyEditsByMonth = plannerSession?.monthlyEditsByMonth || {}
  const weeklyEditsByWeek = plannerSession?.weeklyEditsByWeek || {}
  const weekToMonthDayCounts = plannerSession?.weekToMonthDayCounts || new Map()
  const weeklyActualSalesByWeek = plannerSession?.weeklyActualSalesByWeek || {}
  const weeklyActualInflowsByMonth = useMemo(() => buildMonthlyActualInflowsFromWeeklyPeriods({
    weeklyPeriodData,
    weekToMonthDayCounts,
  }), [weeklyPeriodData, weekToMonthDayCounts])

  const handleCellChange = (monthIndex, field, value) => {
    console.log('[planner][watch] monthly handleCellChange FIRED', {
      monthIndex,
      field,
      value,
      epochMonth,
      locationClassId,
    })

    // If we have a daily-canonical model, mutate daily first.
    const ps = plannerSession
    const hasDaily = ps?.dailyByDate && typeof ps.dailyByDate?.get === 'function' && ps?.datesByMonth && typeof ps.datesByMonth?.get === 'function'
    const draftOverrides = {}
    const patchOverrides = {}

    if (hasDaily) {
      const target = data?.[monthIndex]
      const em = target?.epochMonth
      const dates = em ? (ps.datesByMonth.get(String(em)) || []) : []
      console.log('[planner][watch] monthly daily-source status', {
        em,
        hasDaily,
        datesLen: dates.length,
        dailySize: ps?.dailyByDate?.size || 0,
      })

      if (dates.length) {
        const activeCalcMode = plannerSession?.calcMode || 'allocate'
        const start = Number(epochMonth || 0)
        const annualEpochMonths = Array.from({ length: 12 }, (_, i) => String(start + i))
        const annualOffsetDates = annualEpochMonths.flatMap((monthKey) => {
          if (String(monthKey) === String(em)) return []
          return ps.datesByMonth.get(String(monthKey)) || []
        })
        const fallbackRows = Array.isArray(mergedData) && mergedData.length
          ? mergedData
          : (Array.isArray(effectiveMonthly) && effectiveMonthly.length ? effectiveMonthly : data)
        const buildResolvedMonthMetrics = (nextDaily) => {
          const nextMonthRoll = rollupDailyToMonths({ byDate: nextDaily, epochMonths: annualEpochMonths })
          const firstDate = ps.datesByMonth.get(String(em))?.[0] || null
          const nextSalesPlan = resolveMonthFieldValue({
            epochMonth: em,
            monthRoll: nextMonthRoll,
            datesByMonth: ps.datesByMonth,
            fallbackRows,
            field: 'salesPlan',
          })
          const nextAnnualSales = resolveAnnualMonthTotal({
            epochMonths: annualEpochMonths,
            monthRoll: nextMonthRoll,
            datesByMonth: ps.datesByMonth,
            fallbackRows,
            field: 'salesPlan',
          })
          const nextMarkdownPlan = Math.round(resolveMonthFieldValue({
            epochMonth: em,
            monthRoll: nextMonthRoll,
            datesByMonth: ps.datesByMonth,
            fallbackRows,
            field: 'markdownPlan',
          }))
          const nextAnnualMarkdown = resolveAnnualMonthTotal({
            epochMonths: annualEpochMonths,
            monthRoll: nextMonthRoll,
            datesByMonth: ps.datesByMonth,
            fallbackRows,
            field: 'markdownPlan',
          })
          const nextPlanInvBOM = firstDate
            ? Number(nextDaily?.get?.(firstDate)?.inventory ?? 0)
            : resolveMonthFieldValue({
              epochMonth: em,
              monthRoll: nextMonthRoll,
              datesByMonth: ps.datesByMonth,
              fallbackRows,
              field: 'planInvBOM',
            })

          return {
            nextSalesPlan,
            nextPlanPercent: nextAnnualSales > 0 ? ((nextSalesPlan / nextAnnualSales) * 100) : 0,
            nextMarkdownPlan,
            nextPlanInvBOM,
            nextPlanSS: nextSalesPlan > 0 ? (nextPlanInvBOM / nextSalesPlan) : 0,
            nextMarkdownPlanPercent: deriveMonthlyMarkdownPercent({
              markdownPlan: nextMarkdownPlan,
              annualMarkdownPlan: nextAnnualMarkdown,
            }),
          }
        }

        if (field === 'salesPlan') {
          const desiredSales = Math.max(0, Number(value || 0))
          const nextDaily = activeCalcMode === 'allocate'
            ? applyEditAllocateScopeLocked({
              byDate: ps.dailyByDate,
              datesEdited: dates,
              datesToOffset: annualOffsetDates,
              field: 'salesPlan',
              newTotal: desiredSales,
              clampMin: 0,
            })
            : applyEditAggregate({
              byDate: ps.dailyByDate,
              dates,
              field: 'salesPlan',
              newTotal: desiredSales,
              clampMin: 0,
            })

          const resolved = buildResolvedMonthMetrics(nextDaily)
          draftOverrides.salesPlan = resolved.nextSalesPlan
          draftOverrides.planPercent = resolved.nextPlanPercent
          draftOverrides.planInvBOM = resolved.nextPlanInvBOM
          draftOverrides.planSS = resolved.nextPlanSS
          patchOverrides.salesPlan = resolved.nextSalesPlan
          patchOverrides.planPercent = resolved.nextPlanPercent
          patchOverrides.planInvBOM = resolved.nextPlanInvBOM
          patchOverrides.planSS = resolved.nextPlanSS

          if (typeof ps?.setDailyByDate === 'function' && nextDaily) {
            ps.setDailyByDate(nextDaily)
          }
        }

        // Sales edits: monthly view edits plan% and now also supports direct $ edits.
        if (field === 'planPercent') {
          // Translate planPercent into a target month sales total.

          const monthRoll = rollupDailyToMonths({ byDate: ps.dailyByDate, epochMonths: annualEpochMonths })
          const annual = resolveAnnualMonthTotal({
            epochMonths: annualEpochMonths,
            monthRoll,
            datesByMonth: ps.datesByMonth,
            fallbackRows,
            field: 'salesPlan',
          })

          const pct = Number(value || 0) / 100
          const desired = annual * pct
          const nextDaily = activeCalcMode === 'allocate'
            ? applyEditAllocateScopeLocked({
              byDate: ps.dailyByDate,
              datesEdited: dates,
              datesToOffset: annualOffsetDates,
              field: 'salesPlan',
              newTotal: Math.max(0, desired),
              clampMin: 0,
            })
            : applyEditAggregate({
              byDate: ps.dailyByDate,
              dates,
              field: 'salesPlan',
              newTotal: Math.max(0, desired),
              clampMin: 0,
            })

          const resolved = buildResolvedMonthMetrics(nextDaily)
          draftOverrides.salesPlan = resolved.nextSalesPlan
          draftOverrides.planPercent = resolved.nextPlanPercent
          draftOverrides.planInvBOM = resolved.nextPlanInvBOM
          draftOverrides.planSS = resolved.nextPlanSS
          patchOverrides.salesPlan = resolved.nextSalesPlan
          patchOverrides.planPercent = resolved.nextPlanPercent
          patchOverrides.planInvBOM = resolved.nextPlanInvBOM
          patchOverrides.planSS = resolved.nextPlanSS

          if (typeof ps?.setDailyByDate === 'function' && nextDaily) {
            ps.setDailyByDate(nextDaily)
          }
        }

        // Markdowns edits: allow editing % or $; $ is canonical daily.
        if (field === 'markdownPlan') {
          const roundedMarkdown = Math.max(0, Math.round(Number(value || 0)))
          const nextDaily = activeCalcMode === 'allocate'
            ? applyEditAllocateScopeLocked({
              byDate: ps.dailyByDate,
              datesEdited: dates,
              datesToOffset: annualOffsetDates,
              field: 'markdownPlan',
              newTotal: roundedMarkdown,
              clampMin: 0,
            })
            : applyEditAggregate({
              byDate: ps.dailyByDate,
              dates,
              field: 'markdownPlan',
              newTotal: roundedMarkdown,
              clampMin: 0,
            })
          const resolved = buildResolvedMonthMetrics(nextDaily)
          draftOverrides.markdownPlan = resolved.nextMarkdownPlan
          draftOverrides.markdownPlanPercent = resolved.nextMarkdownPlanPercent
          patchOverrides.markdownPlan = resolved.nextMarkdownPlan
          patchOverrides.markdownPlanPercent = resolved.nextMarkdownPlanPercent
          if (typeof ps?.setDailyByDate === 'function') ps.setDailyByDate(nextDaily)
        }

        if (field === 'markdownPlanPercent') {
          const monthRoll = rollupDailyToMonths({ byDate: ps.dailyByDate, epochMonths: annualEpochMonths })
          const annualMarkdown = resolveAnnualMonthTotal({
            epochMonths: annualEpochMonths,
            monthRoll,
            datesByMonth: ps.datesByMonth,
            fallbackRows,
            field: 'markdownPlan',
          })
          const desiredMd = Math.round(deriveMonthlyMarkdownValue({
            markdownPlanPercent: Number(value || 0),
            annualMarkdownPlan: annualMarkdown,
          }))
          const nextDaily = activeCalcMode === 'allocate'
            ? applyEditAllocateScopeLocked({
              byDate: ps.dailyByDate,
              datesEdited: dates,
              datesToOffset: annualOffsetDates,
              field: 'markdownPlan',
              newTotal: Math.max(0, desiredMd),
              clampMin: 0,
            })
            : applyEditAggregate({
              byDate: ps.dailyByDate,
              dates,
              field: 'markdownPlan',
              newTotal: Math.max(0, desiredMd),
              clampMin: 0,
            })
          const resolved = buildResolvedMonthMetrics(nextDaily)
          draftOverrides.markdownPlan = resolved.nextMarkdownPlan
          draftOverrides.markdownPlanPercent = resolved.nextMarkdownPlanPercent
          patchOverrides.markdownPlan = resolved.nextMarkdownPlan
          patchOverrides.markdownPlanPercent = resolved.nextMarkdownPlanPercent
          if (typeof ps?.setDailyByDate === 'function') ps.setDailyByDate(nextDaily)
        }

        // Inventory BOM edits: beginning-of-month inventory = first day in month.
        if (field === 'planInvBOM') {
          const first = dates[0]
          const nextDaily = new Map(ps.dailyByDate)
          const rec = nextDaily.get(first)
          if (rec) nextDaily.set(first, { ...rec, inventory: Math.max(0, Number(value || 0)) })
          const resolved = buildResolvedMonthMetrics(nextDaily)
          draftOverrides.planInvBOM = resolved.nextPlanInvBOM
          draftOverrides.planSS = resolved.nextPlanSS
          patchOverrides.planInvBOM = resolved.nextPlanInvBOM
          patchOverrides.planSS = resolved.nextPlanSS
          if (typeof ps?.setDailyByDate === 'function') ps.setDailyByDate(nextDaily)
        }

        // Plan S/S edits imply a target BOM inventory for the month.
        if (field === 'planSS') {
          const curSalesTotal = dates.reduce((acc, dt) => acc + Number(ps.dailyByDate.get(dt)?.salesPlan || 0), 0)
          const desiredBom = Math.max(0, curSalesTotal * Number(value || 0))
          const first = dates[0]
          const nextDaily = new Map(ps.dailyByDate)
          const rec = nextDaily.get(first)
          if (rec) nextDaily.set(first, { ...rec, inventory: desiredBom })
          const resolved = buildResolvedMonthMetrics(nextDaily)
          draftOverrides.planInvBOM = resolved.nextPlanInvBOM
          draftOverrides.planSS = resolved.nextPlanSS
          patchOverrides.planInvBOM = resolved.nextPlanInvBOM
          patchOverrides.planSS = resolved.nextPlanSS
          if (typeof ps?.setDailyByDate === 'function') ps.setDailyByDate(nextDaily)
        }
      }
    }

    const currentRow = (Array.isArray(mergedData) && mergedData[monthIndex])
      || (Array.isArray(data) && data[monthIndex])
      || {}

    if (field === 'planSS' && typeof draftOverrides.planInvBOM === 'undefined') {
      const pairedValues = derivePlanInventoryPairFromEdit({
        salesPlan: currentRow?.salesPlan,
        field,
        value,
      })
      draftOverrides.planInvBOM = Number(pairedValues?.planInvBOM || 0)
      draftOverrides.planSS = Number(pairedValues?.planSS || 0)
      patchOverrides.planInvBOM = draftOverrides.planInvBOM
      patchOverrides.planSS = draftOverrides.planSS
    }

    if (field === 'planInvBOM' && typeof draftOverrides.planSS === 'undefined') {
      const pairedValues = derivePlanInventoryPairFromEdit({
        salesPlan: currentRow?.salesPlan,
        field,
        value,
      })
      draftOverrides.planInvBOM = Number(pairedValues?.planInvBOM || 0)
      draftOverrides.planSS = Number(pairedValues?.planSS || 0)
      patchOverrides.planInvBOM = draftOverrides.planInvBOM
      patchOverrides.planSS = draftOverrides.planSS
    }

    // Existing local draft (for immediate UI response)
    setDraft((prev) => {
      const next = { ...prev }
      const setAt = (k, nextValue = value) => {
        const arr = [...(next[k] || [])]
        arr[monthIndex] = nextValue
        next[k] = arr
      }
      if (field === 'salesPlan') setAt('salesPlan')
      if (field === 'planPercent') setAt('planPercent')
      if (field === 'planSS') setAt('planSS')
      if (field === 'planInvBOM') setAt('planInvBOM')
      if (field === 'markdownPlanPercent') setAt('markdownPlanPercent')
      if (field === 'markdownPlan') setAt('markdownPlan')
      Object.entries(draftOverrides).forEach(([key, nextValue]) => setAt(key, nextValue))
      return next
    })
    setDirty(true)

    // Also push into session model w/ timestamp.
    const target = data?.[monthIndex]
    const em = target?.epochMonth
    if (em && typeof onMonthlyEdit === 'function') {
      const patch = {}
      if (field === 'salesPlan') patch.salesPlan = Number(value)
      if (field === 'planPercent') patch.planPercent = Number(value)
      if (field === 'planSS') patch.planSS = Number(value)
      if (field === 'planInvBOM') patch.planInvBOM = Number(value)
      if (field === 'markdownPlanPercent') patch.markdownPlanPercent = Number(value)
      if (field === 'markdownPlan') patch.markdownPlan = Number(value)
      Object.assign(patch, patchOverrides)
      console.log('[planner][watch] monthly calling onMonthlyEdit', { em, patch })
      onMonthlyEdit(em, patch)
    } else {
      console.log('[planner][watch] monthly skipped onMonthlyEdit', {
        em,
        hasCallback: typeof onMonthlyEdit === 'function',
      })
    }
  }

  // Effective monthly values = base + monthly edits, and weekly edits rolled up by day weights.
  const monthlyBase = data
  const effectiveMonthly = useMemo(() => {
    // Convert month edits keyed by epochMonth to internal format.
    const monthEdits = {}
    Object.entries(monthlyEditsByMonth || {}).forEach(([k, v]) => {
      monthEdits[Number(k)] = v
    })

    return rollupWeeklyToMonthly({
      monthlyBase,
      monthlyEdits: monthEdits,
      weeklyEdits: weeklyEditsByWeek,
      weekToMonthDayCounts,
      weeklyActualSalesByWeek,
      calcMode: plannerSession?.calcMode || 'allocate',
    })
  }, [monthlyBase, weeklyEditsByWeek, monthlyEditsByMonth, weekToMonthDayCounts, weeklyActualSalesByWeek, plannerSession?.calcMode])

  const mergedData = useMemo(() => {
    // If daily-canonical draft exists, derive plan dollars from daily (not from BQ row values).
    const ps = plannerSession
    const hasDaily = ps?.dailyByDate && typeof ps.dailyByDate?.values === 'function'

    let byMonth = null
    let bomByMonth = null
    let annualSales = null
    let hasDailyByMonth = null
    if (hasDaily) {
      const start = Number(epochMonth || 0)
      const epochMonths = Array.from({ length: 12 }, (_, i) => String(start + i))
      byMonth = rollupDailyToMonths({ byDate: ps.dailyByDate, epochMonths })


      bomByMonth = {}
      hasDailyByMonth = {}
      epochMonths.forEach((em) => {
        const dates = ps?.datesByMonth?.get?.(String(em)) || []
        const first = Array.isArray(dates) && dates.length ? dates[0] : null
        hasDailyByMonth[String(em)] = !!first
        bomByMonth[String(em)] = first ? Number(ps.dailyByDate.get(first)?.inventory ?? 0) : 0
      })
    }

    const baseRows = (effectiveMonthly || []).map((m, idx) => {
      const em = String(m?.epochMonth)
      const monthHasDaily = !!hasDailyByMonth?.[em]
      const dailySales = Number(byMonth?.[em]?.salesPlan || 0)
      const dailyMarkdown = Number(byMonth?.[em]?.markdownPlan || 0)
      const dailyBom = Number(bomByMonth?.[em] ?? 0)
      const hasExplicitInventoryOverride = hasExplicitPlanInventoryOverrideForMonth({
        epochMonth: em,
        monthlyEditsByMonth,
        weeklyEditsByWeek,
        weekToMonthDayCounts,
      })
      const useDailyForMonth = shouldUseDailyMonthValues({
        monthHasDaily,
        dailySales,
        dailyMarkdown,
        dailyBom,
        monthlyRow: m,
      })

      const derivedSales = useDailyForMonth ? dailySales : Number(m.salesPlan || 0)
      const derivedMd = useDailyForMonth ? dailyMarkdown : Number(m.markdownPlan || 0)
      const derivedBom = (useDailyForMonth && !hasExplicitInventoryOverride) ? dailyBom : Number(m.planInvBOM || 0)

      const salesPlan = Number(draft?.salesPlan?.[idx] ?? derivedSales)
      const markdownPlan = Number(draft?.markdownPlan?.[idx] ?? derivedMd)
      const planInvBOM = Number(draft?.planInvBOM?.[idx] ?? derivedBom)

      return {
        ...m,
        salesPlan,
        markdownPlan,
        planInvBOM,
      }
    })

    const staticAnnualSales = baseRows.reduce((acc, row) => {
      if (acc > 0) return acc
      const v = Number(row?.salesAnnualPlan ?? 0)
      return v > 0 ? v : acc
    }, 0)
    annualSales = staticAnnualSales > 0
      ? staticAnnualSales
      : baseRows.reduce((acc, row) => acc + Number(row?.salesPlan || 0), 0)

    const rowsOut = baseRows.map((row, idx) => {
      const hasDirectMarkdownDraft = typeof draft?.markdownPlan?.[idx] !== 'undefined'
      const hasMarkdownPercentDraft = typeof draft?.markdownPlanPercent?.[idx] !== 'undefined'
      const salesPlan = Number(row?.salesPlan || 0)
      const annualMarkdownReference = sumMonthlyMarkdownPlan(baseRows)
      const markdownPlan = hasDirectMarkdownDraft
        ? Number(row?.markdownPlan || 0)
        : hasMarkdownPercentDraft
          ? deriveMonthlyMarkdownValue({
            markdownPlanPercent: Number(draft?.markdownPlanPercent?.[idx] || 0),
            annualMarkdownPlan: annualMarkdownReference,
          })
          : Number(row?.markdownPlan || 0)
      const planInvBOM = Number(row?.planInvBOM || 0)
      const derivedPlanPercent = annualSales ? (salesPlan / annualSales) * 100 : 0
      const derivedPlanSS = salesPlan ? (planInvBOM / salesPlan) : 0

      return {
        ...row,
        markdownPlan,
        planPercent: Number(draft?.planPercent?.[idx] ?? derivedPlanPercent),
        planSS: Number(draft?.planSS?.[idx] ?? derivedPlanSS),
      }
    })

    const annualMarkdownPlan = sumMonthlyMarkdownPlan(rowsOut)

    const rowsWithMarkdownPercents = rowsOut.map((row, idx) => ({
      ...row,
      markdownPlanPercent: Number(
        typeof draft?.markdownPlanPercent?.[idx] !== 'undefined'
          ? draft?.markdownPlanPercent?.[idx]
          : deriveMonthlyMarkdownPercent({
            markdownPlan: Number(row?.markdownPlan || 0),
            annualMarkdownPlan,
          })
      ),
    }))

    const inflows = deriveMonthlyInflows(rowsWithMarkdownPercents)
    const rowsWithInflows = rowsWithMarkdownPercents.map((row, idx) => {
      const weeklyActualInflows = weeklyActualInflowsByMonth.get(String(row?.epochMonth || '')) || null
      const legacyActualRow = Array.isArray(legacyActualRows) ? legacyActualRows[idx] : null
      const actualInvBOM = hasMeaningfulActualValue(legacyActualRow?.actualInvBOM)
        ? Number(legacyActualRow?.actualInvBOM || 0)
        : Number(row?.actualInvBOM ?? 0)
      const planInvBOM = Number(row?.planInvBOM ?? 0)
      const salesActual = hasMeaningfulActualValue(legacyActualRow?.salesActual)
        ? Number(legacyActualRow?.salesActual || 0)
        : Number(row?.salesActual ?? 0)
      const markdownActual = hasMeaningfulActualValue(legacyActualRow?.markdownActual)
        ? Number(legacyActualRow?.markdownActual || 0)
        : Number(row?.markdownActual ?? 0)
      const bowInventory = actualInvBOM > 0 ? actualInvBOM : planInvBOM
      const inflowRetailActual = hasMeaningfulActualValue(legacyActualRow?.inflowRetailActual)
        ? Number(legacyActualRow?.inflowRetailActual || 0)
        : hasMeaningfulActualValue(row?.inflowRetailActual)
          ? Number(row?.inflowRetailActual || 0)
          : Number(weeklyActualInflows?.inflowRetailActual || 0)
      const inflowCostActual = hasMeaningfulActualValue(legacyActualRow?.inflowCostActual)
        ? Number(legacyActualRow?.inflowCostActual || 0)
        : hasMeaningfulActualValue(row?.inflowCostActual)
          ? Number(row?.inflowCostActual || 0)
          : Number(weeklyActualInflows?.inflowCostActual || 0)
      const freshness = idx >= 3
        ? calculateMonthlyFreshnessFactor(
          bowInventory,
          inflows.slice(Math.max(0, idx - 3), idx).map((inflow) => Number(inflow?.planInflowRetail || 0))
        )
        : null

      return {
        ...row,
        salesActual,
        markdownActual,
        actualInvBOM,
        actualSS: salesActual ? (actualInvBOM / salesActual) : Number(row?.actualSS || 0),
        inflowRetailActual,
        inflowCostActual,
        planInflowRetail: Number(inflows[idx]?.planInflowRetail || 0),
        planInflowCost: Number(inflows[idx]?.planInflowCost || 0),
        freshness,
      }
    })

    const totalSalesActual = rowsWithInflows.reduce((acc, row) => acc + Number(row?.salesActual || 0), 0)
    const totalMdActual = rowsWithInflows.reduce((acc, row) => acc + Number(row?.markdownActual || 0), 0)

    return rowsWithInflows.map((row) => ({
      ...row,
      actualPercent: totalSalesActual ? (Number(row?.salesActual || 0) / totalSalesActual) * 100 : 0,
      markdownActualPercent: totalMdActual ? (Number(row?.markdownActual || 0) / totalMdActual) * 100 : 0,
    }))
  }, [
    effectiveMonthly,
    draft,
    legacyActualRows,
    plannerSession?.dailyByDate,
    plannerSession?.datesByMonth,
    weeklyActualInflowsByMonth,
  ])

  // Drive the left-panel chart with the live-edited merged data so any cell
  // edit immediately updates the chart — not just new API fetches.
  const lastMergedDataSigRef = useRef('')
  useEffect(() => {
    const sig = Array.isArray(mergedData)
      ? mergedData.map((d) => `${d?.epochMonth ?? ''}:${Number(d?.salesPlan ?? 0)}:${Number(d?.markdownPlan ?? 0)}:${Number(d?.planInvBOM ?? 0)}:${Number(d?.planInflowRetail ?? 0)}`).join('|')
      : ''
    if (sig === lastMergedDataSigRef.current) return
    lastMergedDataSigRef.current = sig
    if (typeof onMonthlyBaseUi === 'function') onMonthlyBaseUi(mergedData)
  }, [mergedData])

  const summary = useMemo(() => {
    return buildMonthlySummaryValues(mergedData)
  }, [mergedData])

  const annualValues = useMemo(() => {
    const baseAnnualValues = buildPlannerAnnualValues({
      planRows: mergedData,
      actualRows: mergedData,
    })
    const resolvedTrendedTurn = resolveMonthlyHeaderTrendedTurn({
      weeklyPeriodData,
      fallbackAnnualValues: baseAnnualValues,
    })

    return {
      ...baseAnnualValues,
      trendedTurn: resolvedTrendedTurn.value,
      trendedTurnSource: resolvedTrendedTurn.source,
    }
  }, [mergedData, weeklyPeriodData])

  const commitDailyMutation = (nextDaily) => {
    if (nextDaily && typeof plannerSession?.setDailyByDate === 'function') {
      plannerSession.setDailyByDate(nextDaily)
      setDirty(true)
    }
  }

  const copyActualsToPlan = (kind) => {
    const ps = plannerSession
    if (!ps?.dailyByDate || typeof ps.dailyByDate.get !== 'function' || !ps?.datesByMonth || typeof ps.datesByMonth.get !== 'function') {
      messageContext.show('Planner daily context is not ready yet')
      return
    }

    let nextDaily = ps.dailyByDate
    const totalPlanSales = mergedData.reduce((acc, row) => acc + Number(row?.salesPlan || 0), 0)
    const totalPlanMarkdown = mergedData.reduce((acc, row) => acc + Number(row?.markdownPlan || 0), 0)
    const totalActualSales = mergedData.reduce((acc, row) => acc + Number(row?.salesActual || 0), 0)
    const totalActualMarkdown = mergedData.reduce((acc, row) => acc + Number(row?.markdownActual || 0), 0)

    if (kind === 'sales' && totalPlanSales > 0 && totalActualSales > 0) {
      mergedData.forEach((row) => {
        const dates = ps.datesByMonth.get(String(row.epochMonth)) || []
        if (!dates.length) return
        const desiredSales = totalPlanSales * (Number(row?.salesActual || 0) / totalActualSales)
        nextDaily = applyEditAggregate({ byDate: nextDaily, dates, field: 'salesPlan', newTotal: Math.max(0, desiredSales), clampMin: 0 })
        const mdRate = Number(row?.salesPlan || 0) ? (Number(row?.markdownPlan || 0) / Number(row?.salesPlan || 0)) : 0
        const desiredMd = desiredSales * mdRate
        nextDaily = applyEditAggregate({ byDate: nextDaily, dates, field: 'markdownPlan', newTotal: Math.max(0, desiredMd), clampMin: 0 })
        nextDaily = applyFirstDayInventory(nextDaily, dates, desiredSales * Number(row?.planSS || 0))
      })
      commitDailyMutation(nextDaily)
      return
    }

    if (kind === 'inventory') {
      mergedData.forEach((row) => {
        const dates = ps.datesByMonth.get(String(row.epochMonth)) || []
        if (!dates.length) return
        nextDaily = applyFirstDayInventory(nextDaily, dates, Number(row?.salesPlan || 0) * Number(row?.actualSS || 0))
      })
      commitDailyMutation(nextDaily)
      return
    }

    if (kind === 'markdown' && totalPlanMarkdown > 0 && totalActualMarkdown > 0) {
      mergedData.forEach((row) => {
        const dates = ps.datesByMonth.get(String(row.epochMonth)) || []
        if (!dates.length) return
        const desiredMd = totalPlanMarkdown * (Number(row?.markdownActual || 0) / totalActualMarkdown)
        nextDaily = applyEditAggregate({ byDate: nextDaily, dates, field: 'markdownPlan', newTotal: Math.max(0, desiredMd), clampMin: 0 })
      })
      commitDailyMutation(nextDaily)
    }
  }

  const applyAnnualMonthMutations = ({ buildPatch, buildDraft, mutateDailyForMonth = null }) => {
    const ps = plannerSession
    const rows = Array.isArray(mergedData) ? mergedData : []
    if (!rows.length) return

    let nextDaily = ps?.dailyByDate
    let didMutateDaily = false
    const nextDraftState = typeof buildDraft === 'function' ? buildDraft(rows) : null

    rows.forEach((row, idx) => {
      const epochMonthKey = String(row?.epochMonth ?? '')
      const dates = ps?.datesByMonth?.get?.(epochMonthKey) || []
      const patch = typeof buildPatch === 'function' ? buildPatch(row, idx, rows) : null

      if (patch && row?.epochMonth && typeof onMonthlyEdit === 'function') {
        onMonthlyEdit(row.epochMonth, patch)
      }

      if (typeof mutateDailyForMonth === 'function' && Array.isArray(dates) && dates.length && nextDaily && typeof nextDaily.get === 'function') {
        const mutated = mutateDailyForMonth({ nextDaily, row, idx, rows, dates, patch })
        if (mutated && mutated !== nextDaily) {
          nextDaily = mutated
          didMutateDaily = true
        }
      }
    })

    if (nextDraftState) {
      setDraft((prev) => ({ ...prev, ...nextDraftState }))
    }

    if (didMutateDaily) {
      commitDailyMutation(nextDaily)
    } else {
      setDirty(true)
    }
  }

  const applyAnnualSales = (newAnnualSales) => {
    if (!Array.isArray(mergedData) || !mergedData.length) return
    const targets = buildAnnualSalesTargets(mergedData, newAnnualSales)
    applyAnnualMonthMutations({
      buildPatch: (row, idx) => {
        return targets[idx] || null
      },
      buildDraft: (liveRows) => {
        const liveTargets = buildAnnualSalesTargets(liveRows, newAnnualSales)
        const salesPlan = []
        const markdownPlan = []
        const markdownPlanPercent = []
        const planInvBOM = []
        const planPercent = []
        const planSS = []
        liveTargets.forEach((target, idx) => {
          salesPlan[idx] = Number(target?.salesPlan || 0)
          markdownPlan[idx] = Number(target?.markdownPlan || 0)
          markdownPlanPercent[idx] = Number(target?.markdownPlanPercent || 0)
          planInvBOM[idx] = Number(target?.planInvBOM || 0)
          planPercent[idx] = Number(target?.planPercent || 0)
          planSS[idx] = Number(target?.planSS || 0)
        })
        return { salesPlan, markdownPlan, markdownPlanPercent, planInvBOM, planPercent, planSS }
      },
      mutateDailyForMonth: ({ nextDaily, dates, patch }) => {
        let updated = applyEditAggregate({ byDate: nextDaily, dates, field: 'salesPlan', newTotal: Math.max(0, Number(patch?.salesPlan || 0)), clampMin: 0 })
        updated = applyEditAggregate({ byDate: updated, dates, field: 'markdownPlan', newTotal: Math.max(0, Number(patch?.markdownPlan || 0)), clampMin: 0 })
        updated = applyFirstDayInventory(updated, dates, Math.max(0, Number(patch?.planInvBOM || 0)))
        return updated
      },
    })
  }

  const applyAnnualMarkdowns = (newPct) => {
    if (!Array.isArray(mergedData) || !mergedData.length) return
    const targets = buildAnnualMarkdownTargets(mergedData, newPct)
    applyAnnualMonthMutations({
      buildPatch: (row, idx) => {
        const target = targets[idx] || {}
        return {
          markdownPlan: Number(target?.markdownPlan || 0),
          markdownPlanPercent: Number(target?.markdownPlanPercent || 0),
        }
      },
      buildDraft: () => {
        const markdownPlan = []
        const markdownPlanPercent = []
        targets.forEach((target, idx) => {
          markdownPlan[idx] = Number(target?.markdownPlan || 0)
          markdownPlanPercent[idx] = Number(target?.markdownPlanPercent || 0)
        })
        return { markdownPlan, markdownPlanPercent }
      },
      mutateDailyForMonth: ({ nextDaily, dates, patch }) => applyEditAggregate({
        byDate: nextDaily,
        dates,
        field: 'markdownPlan',
        newTotal: Math.max(0, Number(patch?.markdownPlan || 0)),
        clampMin: 0,
      }),
    })
  }

  const applyAnnualTurn = (newTurn) => {
    if (!Array.isArray(mergedData) || !mergedData.length) return
    const targets = buildAnnualTurnTargets(mergedData, newTurn)
    applyAnnualMonthMutations({
      buildPatch: (row, idx) => {
        const target = targets[idx] || {}
        return {
          planInvBOM: Number(target?.planInvBOM || 0),
          planSS: Number(target?.planSS || 0),
        }
      },
      buildDraft: () => {
        const planInvBOM = []
        const planSS = []
        targets.forEach((target, idx) => {
          planInvBOM[idx] = Number(target?.planInvBOM || 0)
          planSS[idx] = Number(target?.planSS || 0)
        })
        return { planInvBOM, planSS }
      },
      mutateDailyForMonth: ({ nextDaily, dates, patch }) => applyFirstDayInventory(nextDaily, dates, Math.max(0, Number(patch?.planInvBOM || 0))),
    })
  }

  const applyAnnualImu = (newImuPct) => {
    const imuRate = toImuRate(newImuPct)
    applyAnnualMonthMutations({
      buildPatch: () => ({ imuPlan: imuRate }),
      buildDraft: () => null,
      mutateDailyForMonth: null,
    })
  }

  const rebalanceNegativeInflows = (rows) => {
    const tempRows = (Array.isArray(rows) ? rows : []).map((row) => ({ ...row }))
    // Run 2 passes to handle wrap-around cascades (e.g., last month pushes deficit to month 0
    // which then cascades forward again in pass 2).
    const maxPasses = 2
    for (let pass = 0; pass < maxPasses; pass++) {
      for (let i = 0; i < tempRows.length; i++) {
        const inflows = deriveMonthlyInflows(tempRows)
        const currentInflow = Number(inflows[i]?.planInflowRetail || 0)
        if (currentInflow < 0) {
          const nextIndex = i + 1 === tempRows.length ? 0 : i + 1
          tempRows[nextIndex] = {
            ...tempRows[nextIndex],
            planInvBOM: Number(tempRows[nextIndex]?.planInvBOM || 0) + Math.abs(currentInflow),
          }
        }
      }
    }
    return tempRows
  }

  const commitMonthlyBomRows = (rowsToCommit) => {
    const ps = plannerSession
    if (!ps?.dailyByDate || typeof ps.dailyByDate.get !== 'function' || !ps?.datesByMonth || typeof ps.datesByMonth.get !== 'function') {
      return // daily model unavailable — caller handles fallback via draft/session
    }

    let nextDaily = ps.dailyByDate
    let anyUpdated = false
    ;(Array.isArray(rowsToCommit) ? rowsToCommit : []).forEach((row) => {
      const dates = ps.datesByMonth.get(String(row.epochMonth)) || []
      if (!dates.length) return
      nextDaily = applyFirstDayInventory(nextDaily, dates, Number(row?.planInvBOM || 0))
      anyUpdated = true
    })

    if (anyUpdated) commitDailyMutation(nextDaily)
  }

  // Shared helper: apply rebalanced BOM rows via draft + session + (optionally) daily model.
  // This ensures Balance All Inflow and Simple K Flow work even when daily_values are absent.
  const applyRebalancedBomRows = (rebalancedRows, originalRows) => {
    const safeRebalanced = Array.isArray(rebalancedRows) ? rebalancedRows : []
    const safeOriginal = Array.isArray(originalRows) ? originalRows : []
    if (!safeRebalanced.length) return

    // 1. Update local draft for immediate UI response
    setDraft((prev) => {
      const next = { ...prev }
      const newPlanInvBOM = [...(next.planInvBOM || [])]
      const newPlanSS = [...(next.planSS || [])]
      safeRebalanced.forEach((row, idx) => {
        const newBom = Number(row?.planInvBOM || 0)
        const sales = Number(row?.salesPlan || 0)
        newPlanInvBOM[idx] = newBom
        newPlanSS[idx] = sales > 0 ? Number((newBom / sales).toFixed(2)) : 0
      })
      return { ...next, planInvBOM: newPlanInvBOM, planSS: newPlanSS }
    })

    // 2. Propagate changed months to session (needed for save payload)
    if (typeof onMonthlyEdit === 'function') {
      safeRebalanced.forEach((row, idx) => {
        const origBom = Number(safeOriginal[idx]?.planInvBOM || 0)
        const newBom = Number(row?.planInvBOM || 0)
        if (Math.abs(newBom - origBom) > 0.001) {
          const em = row?.epochMonth
          if (!em) return
          const sales = Number(row?.salesPlan || 0)
          onMonthlyEdit(em, {
            planInvBOM: newBom,
            planSS: sales > 0 ? Number((newBom / sales).toFixed(2)) : 0,
          })
        }
      })
    }

    // 3. Also update canonical daily model if available (no-op when datesByMonth is empty)
    commitMonthlyBomRows(safeRebalanced)

    setDirty(true)
  }

  const handleBalanceAllInflows = () => {
    const rows = Array.isArray(mergedData) ? mergedData : []
    if (!rows.length) return
    const rebalanced = rebalanceNegativeInflows(rows)
    applyRebalancedBomRows(rebalanced, rows)
  }

  const handleSimpleKFlow = () => {
    const rows = Array.isArray(mergedData) ? mergedData : []
    if (!rows.length) return

    // ── Weighted K-Flow — matches the original legacy PlanCalculatorService.getKFlow ──
    // High-sales months receive LESS opening inventory (they turn quickly),
    // low-sales months receive MORE (they need more inventory to carry through).
    // Formula: newBOM[m] = (sales[m] * avg_ss) – (dev_weight[m] * avg_inv)
    //   where dev_weight[m] = (salesPct[m] – avg_sales_pct) / total_positive_deviation
    const { annualSalesPlan, annualTurnPlan } = annualValues

    if (!(annualSalesPlan > 0) || !(annualTurnPlan > 0)) {
      messageContext.show('K-Flow requires Annual Sales and Annual Turn to be set.')
      return
    }

    const avg_ss         = +(12 / annualTurnPlan).toFixed(4)   // months-of-supply
    const turned_inv     = +(annualSalesPlan * avg_ss).toFixed(0)
    const avg_inv        = +(turned_inv / 12).toFixed(0)        // average monthly inventory target
    const avg_sales_per  = +(100 / 12).toFixed(4)              // 8.3333… – even distribution pct

    // Step 1: total positive deviation (months whose sales-share is above average)
    let total_sales_deviation = 0
    rows.forEach((row) => {
      const sales    = Math.max(0, Number(row?.salesPlan || 0))
      const sales_per = (sales / annualSalesPlan) * 100
      const sales_dev = sales_per - avg_sales_per
      if (sales_dev > 0) total_sales_deviation += sales_dev
    })

    // Step 2: compute deviation-weighted BOM for each month
    const seededRows = rows.map((row) => {
      const sales      = Math.max(0, Number(row?.salesPlan || 0))
      const sales_per  = (sales / annualSalesPlan) * 100
      const sales_dev  = +(sales_per - avg_sales_per).toFixed(4)
      const dev_weight = total_sales_deviation !== 0 ? +(sales_dev / total_sales_deviation).toFixed(4) : 0
      const bom_adj    = +((dev_weight / 2) * avg_inv).toFixed(0)  // halved: more smoothing (Adam Hambleton 2026-04-10)
      const inv        = +(sales * avg_ss).toFixed(0)
      const new_inv    = Math.max(0, inv - bom_adj)
      return {
        ...row,
        planInvBOM: new_inv,
        planSS: sales > 0 ? +(new_inv / sales).toFixed(4) : 0,
      }
    })

    applyRebalancedBomRows(rebalanceNegativeInflows(seededRows), rows)
  }

    return (
     <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 1, p: 2 }}>
       <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', mb: 1 }}>
         <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
           <Typography variant="subtitle1">Monthly Plan (New UI)</Typography>
           {loading ? <Chip size="small" label="Loading…" variant="outlined" /> : dirty ? <Chip size="small" label="Unsaved changes" color="warning" /> : <Chip size="small" label={rows.length ? 'Loaded' : 'No data'} variant="outlined" />}
         </Box>
         {/* Save/Update controls live in the global bottom action bar now */}
       </Box>

      <PlanEditMonthlyHeader
        annualValues={annualValues}
        collapsed={annualHeaderCollapsed}
        onCollapsedChange={onAnnualHeaderCollapsedChange}
        onApplyAnnualSales={applyAnnualSales}
        onApplyAnnualMarkdowns={applyAnnualMarkdowns}
        onApplyAnnualTurn={applyAnnualTurn}
        onApplyAnnualImu={applyAnnualImu}
        onBalanceAllInflows={handleBalanceAllInflows}
        onSimpleKFlow={handleSimpleKFlow}
      />

      <MonthlyPlanningGrid
        columns={columns}
        data={mergedData}
        summary={summary}
        onChange={handleCellChange}
        onCopyActuals={copyActualsToPlan}
      />
     </Box>
   )
 }

export default PlanEditMonthlyGridShell
